/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { IGroup } from '@fluentui/react/lib/GroupedList';
import { IStackItemStyles, IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { IToggleProps, IToggleStyles, Toggle } from '@fluentui/react/lib/Toggle';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { CluModelFilteredLabels } from 'src/clu/appShell/CluModelFilteredLabels';
import { CLUTestModelsFiltersBlade, FilterItem } from 'src/clu/appShell/CLUTestModelsFiltersBlade';
import { CluTestSetDetailsTable } from 'src/clu/appShell/CluTestSetDetailsTable';
import { CategoryRow, getTableGroupsAndItems, getUniqueNames, isThereAnyFilter } from 'src/clu/appShell/cluTestSetDetailsTabUtils';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { LuisText } from 'src/core/components/LuisText';
import { TablePagination } from 'src/core/components/TablePagination';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { usePagination } from 'src/core/hooks/usePagination';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { getStylist } from 'src/core/themes/luisStylist';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { TestSetDetailsCommandBar } from 'src/modules/modelGuidance/components/TestSetDetailsCommandBar';

const messages = defineMessages({
    filter: { id: 'clu.cluTestSetDetailsTab.filter', defaultMessage: 'Filter' },
    mismatch: { id: 'clu.cluTestSetDetailsTab.mismatch', defaultMessage: 'Show mismatches only' },
    notPredicted: { id: 'clu.cluTestSetDetailsTab.notPredicted', defaultMessage: 'No Prediction' },
    truePositive: { id: 'clu.cluTestSetDetailsTab.truePositive', defaultMessage: 'True Positive' },
    trueNegative: { id: 'clu.cluTestSetDetailsTab.trueNegative', defaultMessage: 'True Negative' },
    falsePositive: { id: 'clu.cluTestSetDetailsTab.falsePositive', defaultMessage: 'False Positive' },
    falseNegative: { id: 'clu.cluTestSetDetailsTab.falseNegative', defaultMessage: 'False Negative' },
    notLabeled: { id: 'clu.cluTestSetDetailsTab.notLabeled', defaultMessage: 'Unlabeled in test set' },
    showConflicts: { id: 'clu.cluTestSetDetailsTab.showConflicts', defaultMessage: 'Show errors only' },
    showingEntityLabels: { id: 'clu.cluTestSetDetailsTab.showingEntityLabels', defaultMessage: 'Showing entity labels' },
    noUtterancesErrorMsg: { id: 'clu.cluTestSetDetailsTab.noUtterancesErrorMsg', defaultMessage: 'No test set utterances available' },
    showingEntityPredictions: { id: 'clu.cluTestSetDetailsTab.showingEntityPredictions', defaultMessage: 'Showing entity predictions' },
    tabDescription: {
        id: 'clu.cluTestSetDetailsTab.tabDescription',
        defaultMessage: `Select an utterance to review its labels and predictions for intents and entities.
        The information here is static and tied to your model, so it won’t update until you train again. Use the filter to select which types of predictions and errors you want to view.`
    },
    noIncorrectUtterancesErrorMsg: {
        id: 'clu.cluTestSetDetailsTab.noIncorrectUtterancesErrorMsg',
        defaultMessage: 'No incorrect test set utterances available'
    }
});

const { styleDiv } = getStylist('TestSetDetailsTab');
const ModelsTableWindow = styleDiv('ModelsTableWindow', { flex: 1 });
const paginationStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({ root: { margin: '10px 0 !important' } });
const tableWrapperStyles = classNamesFunction<IStackProps, IStackItemStyles>()({
    root: { padding: ' 0 30px 30px 30px', height: '90%' }
});
const toggleStyles = classNamesFunction<IToggleProps, IToggleStyles>()({
    container: { margin: '5px 0 0 10px' },
    label: { fontSize: '14px', fontWeight: 400 },
    root: { marginBottom: '0px', marginRight: '5px', display: 'flex' }
});

type CluTestSetDetailsTabProps = {
    isModalOpen?: boolean;
    filteredLabel?: string;
    selectedRowKey?: string;
    selectedModelName: string;
    filteredPrediction?: string;
    showIncorrectPredictionsOnly?: boolean;
    entityFilteredLabelInTestSetTab?: string;
    intentFilteredLabelInTestSetTab?: string;
    entityFilteredPredictionInTestSetTab?: string;
    intentFilteredPredictionInTestSetTab?: string;
};

export const CluTestSetDetailsTab = observer((props: CluTestSetDetailsTabProps) => {
    const {
        selectedModelName,
        entityFilteredLabelInTestSetTab,
        intentFilteredLabelInTestSetTab,
        entityFilteredPredictionInTestSetTab,
        intentFilteredPredictionInTestSetTab
    } = props;

    const { localize } = useLocalization();
    const { rootStore, dispatcher } = useGlobalState();

    const currentProjectStore = rootStore.currentCluProjectStore;
    const models = currentProjectStore.trainedModelStoreHouse.items ?? [];
    const isInEmptyState = models.length === 0;
    const chosenModel = models.find(m => m.name === selectedModelName);
    const isConversationProject = currentProjectStore.projectType === 'Conversation';
    const verificationItems = models.find(m => m.name === selectedModelName).modelVerificationData;

    const verificationDataTracker = useProgressTracker();

    const resultTypes = [
        localize(messages.trueNegative),
        localize(messages.truePositive),
        localize(messages.falseNegative),
        localize(messages.falsePositive)
    ];

    const [searchQuery, setSearchQuery] = React.useState('');
    const [isFilterBladeHidden, setIsFilterBladeHidden] = React.useState(true);
    const [expandedUtterance, setExpandedUtterance] = React.useState<string[]>([]);
    const [showLabeledAnnotations, setShowLabeledAnnotations] = React.useState<boolean>(true);
    const [showIncorrectPredictions, setShowIncorrectPredictions] = React.useState<boolean>(false);
    const [sortKey, setSortKey] = React.useState<SortKeyType<CategoryRow>>({ key: 'utteranceName', order: 'asc' });

    const [entityLabelsToFilter, setEntityLabelsToFilter] = React.useState<FilterItem[]>([]);
    const [intentLabelsToFilter, setIntentLabelsToFilter] = React.useState<FilterItem[]>([]);
    const [entityPredictionToFilter, setEntityPredictionsToFilter] = React.useState<FilterItem[]>([]);
    const [intentPredictionToFilter, setIntentPredictionsToFilter] = React.useState<FilterItem[]>([]);
    const [resultsToFilter, setResultsToFilter] = React.useState<FilterItem[]>(
        resultTypes.map(result => ({ itemName: result, isChecked: false }))
    );

    const tableData = getTableGroupsAndItems(
        verificationItems,
        entityLabelsToFilter,
        intentLabelsToFilter,
        resultsToFilter,
        entityPredictionToFilter,
        intentPredictionToFilter,
        showIncorrectPredictions,
        searchQuery,
        sortKey
    );

    const { currentPage, setCurrentPage, pageItems, pageCount } = usePagination<IGroup>(tableData.groups, 5);

    const mappedGroups = pageItems?.map(group => ({ ...group, isCollapsed: !expandedUtterance.includes(group.data.key) }));

    const getLabelName = (label: string, isPredicted: boolean) =>
        label === LuisConstants.Clu.NoneLabelInConfusionMatrix
            ? isPredicted
                ? localize(messages.notPredicted)
                : localize(messages.notLabeled)
            : label;

    React.useEffect(() => {
        if (verificationItems) {
            setEntityLabelsToFilter(
                getUniqueNames([
                    localize(messages.notLabeled),
                    ...verificationItems?.reduce(
                        (acc, val) => acc.concat(val?.entitiesResult?.expectedEntities?.map(entity => entity?.category)),
                        []
                    )
                ]).map(label => ({ itemName: label, isChecked: label === getLabelName(entityFilteredLabelInTestSetTab, false) }))
            );

            setIntentLabelsToFilter(
                getUniqueNames([...verificationItems?.map(item => item?.intentsResult?.expectedIntent)]).map(label => ({
                    itemName: label,
                    isChecked: label === getLabelName(intentFilteredLabelInTestSetTab, false)
                }))
            );

            setEntityPredictionsToFilter(
                getUniqueNames([
                    localize(messages.notPredicted),
                    ...verificationItems?.reduce(
                        (acc, val) => acc.concat(val?.entitiesResult?.predictedEntities?.map(entity => entity?.category)),
                        []
                    )
                ]).map(label => ({ itemName: label, isChecked: label === getLabelName(entityFilteredPredictionInTestSetTab, true) }))
            );

            setIntentPredictionsToFilter(
                getUniqueNames([...verificationItems?.map(item => item.intentsResult.predictedIntent)]).map(label => ({
                    itemName: label,
                    isChecked: label === getLabelName(intentFilteredPredictionInTestSetTab, true)
                }))
            );
        }
    }, [
        verificationItems,
        entityFilteredLabelInTestSetTab,
        intentFilteredLabelInTestSetTab,
        entityFilteredPredictionInTestSetTab,
        intentFilteredPredictionInTestSetTab
    ]);

    React.useEffect(() => {
        if (rootStore.currentCluProjectStore && chosenModel) {
            dispatcher.dispatch('getCluTrainedModelVerificationData', {
                toastData: {},
                tracker: verificationDataTracker,
                trainedModelName: selectedModelName
            });
        }
    }, [selectedModelName, rootStore.currentCluProjectStore, chosenModel]);

    const onRemoveIntentLabelFilter = (itemName: string) =>
        setIntentLabelsToFilter(intentLabelsToFilter.map(item => (item.itemName === itemName ? { itemName, isChecked: false } : item)));

    const onRemoveEntityLabelFilter = (itemName: string) =>
        setEntityLabelsToFilter(entityLabelsToFilter.map(item => (item.itemName === itemName ? { itemName, isChecked: false } : item)));

    const onClearIntentLabelFilters = () =>
        setIntentLabelsToFilter(intentLabelsToFilter.map(item => ({ itemName: item.itemName, isChecked: false })));

    const onClearEntityLabelFilters = () =>
        setEntityLabelsToFilter(entityLabelsToFilter.map(item => ({ itemName: item.itemName, isChecked: false })));

    const onRemoveIntentPredictionFilter = (itemName: string) =>
        setIntentPredictionsToFilter(
            intentPredictionToFilter.map(item => (item.itemName === itemName ? { itemName, isChecked: false } : item))
        );

    const onRemoveEntityPredictionFilter = (itemName: string) =>
        setEntityPredictionsToFilter(
            entityPredictionToFilter.map(item => (item.itemName === itemName ? { itemName, isChecked: false } : item))
        );

    const onClearIntentPredictionFilters = () =>
        setIntentPredictionsToFilter(intentPredictionToFilter.map(item => ({ itemName: item.itemName, isChecked: false })));

    const onClearEntityPredictionFilters = () =>
        setEntityPredictionsToFilter(entityPredictionToFilter.map(item => ({ itemName: item.itemName, isChecked: false })));

    const onRemoveResultFilter = (itemName: string) =>
        setResultsToFilter(resultsToFilter.map(item => (item.itemName === itemName ? { itemName, isChecked: false } : item)));

    const onClearResultFilters = () => setResultsToFilter(resultsToFilter.map(item => ({ itemName: item.itemName, isChecked: false })));

    const updateFilteredItems = (
        entityNewLabels: FilterItem[],
        entityNewPredictions: FilterItem[],
        intentNewLabels: FilterItem[],
        intentNewPredictions: FilterItem[],
        newResults: FilterItem[]
    ) => {
        setEntityLabelsToFilter(entityNewLabels);
        setIntentLabelsToFilter(intentNewLabels);
        setEntityPredictionsToFilter(entityNewPredictions);
        setIntentPredictionsToFilter(intentNewPredictions);
        setResultsToFilter(newResults);
    };

    const getPlaceholder = () => {
        if (showIncorrectPredictions) {
            return localize(messages.noIncorrectUtterancesErrorMsg);
        }

        return localize(messages.noUtterancesErrorMsg);
    };

    const getFilteredLabels = () => (
        <CluModelFilteredLabels
            intentLabelsToFilter={intentLabelsToFilter}
            onRemoveIntentLabelFilter={onRemoveIntentLabelFilter}
            onClearIntentLabelFilters={onClearIntentLabelFilters}
            intentPredictionToFilter={intentPredictionToFilter}
            onRemoveIntentPredictionFilter={onRemoveIntentPredictionFilter}
            onClearIntentPredictionFilters={onClearIntentPredictionFilters}
            entityLabelsToFilter={entityLabelsToFilter}
            onRemoveEntityLabelFilter={onRemoveEntityLabelFilter}
            onClearEntityLabelFilters={onClearEntityLabelFilters}
            entityPredictionToFilter={entityPredictionToFilter}
            onRemoveEntityPredictionFilter={onRemoveEntityPredictionFilter}
            onClearEntityPredictionFilters={onClearEntityPredictionFilters}
            resultsToFilter={resultsToFilter}
            onRemoveResultFilter={onRemoveResultFilter}
            onClearResultFilters={onClearResultFilters}
        />
    );

    return (
        <Stack grow tokens={{ childrenGap: 20 }}>
            <TestSetDetailsCommandBar
                query={searchQuery}
                isInEmptyState={isInEmptyState}
                onQueryChange={value => setSearchQuery(value)}
                onFilter={() => setIsFilterBladeHidden(false)}
                showIncorrectPredictions={showIncorrectPredictions}
                incorrectToggleMessage={localize(messages.showConflicts)}
                onChangeShowIncorrectPredictions={() => setShowIncorrectPredictions(!showIncorrectPredictions)}
                getFarItems={defaultItems => [
                    isConversationProject && {
                        key: 'annotationsType',
                        onRender: () => (
                            <Stack horizontal role="menuitem" verticalAlign="center" tokens={{ childrenGap: 8 }}>
                                <Toggle
                                    role="switch"
                                    styles={toggleStyles}
                                    checked={showLabeledAnnotations}
                                    onText={localize(messages.showingEntityLabels)}
                                    offText={localize(messages.showingEntityPredictions)}
                                    onChange={() => setShowLabeledAnnotations(!showLabeledAnnotations)}
                                />
                            </Stack>
                        )
                    },
                    ...defaultItems
                ]}
            />

            <LuisText>{localize(messages.tabDescription)}</LuisText>

            {isThereAnyFilter(
                entityLabelsToFilter,
                intentLabelsToFilter,
                resultsToFilter,
                entityPredictionToFilter,
                intentPredictionToFilter
            ) && getFilteredLabels()}

            {isInEmptyState ? (
                <EmptyStateView message={localize(messages.noUtterancesErrorMsg)} />
            ) : (
                <Stack styles={tableWrapperStyles}>
                    <ModelsTableWindow>
                        <CluTestSetDetailsTable
                            sortKey={sortKey}
                            items={tableData.items}
                            tableGroup={mappedGroups}
                            emptyPlaceholder={getPlaceholder()}
                            expandedUtterancesIds={expandedUtterance}
                            isLoading={verificationDataTracker.isRunning}
                            setExpandedUtterancesIds={setExpandedUtterance}
                            showLabeledAnnotations={showLabeledAnnotations}
                            onSortColumn={column =>
                                setSortKey({ key: column.key as keyof CategoryRow, order: column.isSortedDescending ? 'asc' : 'desc' })
                            }
                        />
                    </ModelsTableWindow>

                    <TablePagination
                        pageCount={pageCount}
                        currentPage={currentPage}
                        containerStyles={paginationStyles}
                        onPageChange={p => setCurrentPage(p.selected)}
                    />
                </Stack>
            )}

            <CLUTestModelsFiltersBlade
                isOpen={!isFilterBladeHidden}
                onFiltersChange={updateFilteredItems}
                resultTypesToFilter={resultsToFilter}
                entityLabelsToFilter={entityLabelsToFilter}
                intentLabelsToFilter={intentLabelsToFilter}
                onDismiss={() => setIsFilterBladeHidden(true)}
                entityPredictionsToFilter={entityPredictionToFilter}
                intentPredictionsToFilter={intentPredictionToFilter}
            />
        </Stack>
    );
});
